import { confirmRegistrationRequest } from 'api/aaaService';
import { makeAutoObservable } from 'mobx';
import { RootStore } from 'store/rootStore';
import { ConfirmRegistrationParams, UserRoleTypes } from 'types/aaaService';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';

interface FormFields extends DefaultFormFields {
  email: FormControl<string>;
  password: FormControl<string>;
  repeatPassword: FormControl<string>;
  isPolicyAccepted: FormControl<boolean>;
  isDataProcessingPolicyAccepted: FormControl<boolean>;
}

export default class RegistrationStore {
  rootStore: RootStore;
  loading = false;
  errorCode: number | null = null;

  // Fields are not optional, but required validations should not be shown for empty fields
  public registrationForm = new FormController<FormFields>({
    email: new FormControl<string>('', { validations: ['isEmailValid'], optional: true }),
    password: new FormControl<string>('', { validations: ['isPasswordStrong'], optional: true }),
    repeatPassword: new FormControl<string>('', {
      customValidations: [
        {
          check: this._doPasswordsMatch.bind(this),
          msg: "Passwords don't match",
        },
      ],
      optional: true,
    }),
    isPolicyAccepted: new FormControl<boolean>(false, { optional: false }),
    isDataProcessingPolicyAccepted: new FormControl<boolean>(false, { optional: false }),
  });

  public get isRegistrationFormValid(): boolean {
    const { email, password, repeatPassword, isPolicyAccepted, isDataProcessingPolicyAccepted } =
      this.registrationForm.fields;

    // Checkboxes are manually validated here because form validation doesn't properly check their required status at the moment.
    return (
      Boolean(
        email.value &&
          password.value &&
          repeatPassword.value &&
          isPolicyAccepted.value &&
          isDataProcessingPolicyAccepted.value,
      ) && this.registrationForm.basicFieldsValidationsPassed
    );
  }

  private _doPasswordsMatch(value: string): boolean {
    return this.registrationForm.fields.password.value && value
      ? this.registrationForm.fields.password.value === value
      : true;
  }

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setErrorCode(error: number | null): void {
    this.errorCode = error;
  }

  public setDefaultEmail(email: string): void {
    this.registrationForm.fields.email.value = email;
  }

  public async confirmRegistration(
    type: UserRoleTypes,
    defaultEmail: string,
    inviteCode: string,
    onSuccessfulRegistration: () => void,
  ): Promise<void> {
    const { email, password } = this.registrationForm.fields;
    const data: ConfirmRegistrationParams = {
      defaultEmail,
      confirmedEmail: email.value,
      password: password.value,
      inviteCode,
      type,
    };
    this.setLoading(true);
    const res = await this.rootStore.requestProcessor.processRequest<string, ConfirmRegistrationParams>(
      confirmRegistrationRequest,
      data,
    );
    if (res) {
      if (res.status === 200) {
        this.rootStore.loginSessionStore.logInUser();
        if (type === 'EE') {
          window.location.href = `https://${process.env.REACT_APP_BROKER_PORTAL_BASE_URL}/`;
        }
        if (type === 'AGENT') {
          // Redirect to login page with change agency view
          onSuccessfulRegistration();
        }
      } else {
        this.setErrorCode(res.status);
      }
    }
    this.setLoading(false);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
